import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Medal, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { clientApi } from '@/lib/adminApi';

// Founder UAT 2026-06 — company-wide PUBLIC recognition feed for Mi Portal.
// Server returns ONLY visibility="public" rows for the caller's tenant.
// Organizational recognition & culture — deliberately NOT a social network
// (no likes, comments, reactions or rankings).

const ACCENTS = [
  { bar: 'bg-blue-500', chip: 'bg-blue-50 text-blue-700 border-blue-200', icon: 'text-blue-500' },
  { bar: 'bg-orange-500', chip: 'bg-orange-50 text-orange-700 border-orange-200', icon: 'text-orange-500' },
  { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'text-emerald-500' },
  { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'text-amber-500' },
  { bar: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200', icon: 'text-rose-500' },
];

export default function MeCompanyRecognitionFeed() {
  const { i18n, t } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    clientApi.myRecognitionFeed(20)
      .then((d) => setItems(Array.isArray(d?.items) ? d.items : []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(isES ? 'es' : 'en', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    } catch { return iso; }
  };

  return (
    <Card className="p-6 shadow-card" data-testid="company-recognition-feed">
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15 text-blue-600 shrink-0">
          <Users className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-semibold text-base" data-testid="company-feed-title">
            {isES ? 'Reconocimientos de nuestra gente' : 'Recognition across our team'}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isES
              ? 'Reconocimientos públicos de toda la organización.'
              : 'Public recognitions across the organization.'}
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="company-feed-empty">
          {isES
            ? 'Aún no hay reconocimientos públicos en tu organización.'
            : 'No public recognitions in your organization yet.'}
        </p>
      ) : (
        <ul className="space-y-3" data-testid="company-feed-list">
          {items.map((r, i) => {
            const a = ACCENTS[i % ACCENTS.length];
            return (
              <li
                key={r.id}
                className="relative overflow-hidden rounded-lg border border-border/70 p-4 pl-5"
                data-testid={`company-feed-item-${r.id}`}
              >
                <span className={`absolute left-0 top-0 bottom-0 w-1 ${a.bar}`} aria-hidden="true" />
                <div className="flex items-start gap-3">
                  <Medal className={`h-5 w-5 shrink-0 mt-0.5 ${a.icon}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" data-testid={`feed-recipient-${r.id}`}>{r.to_user_name}</span>
                      <Badge variant="outline" className={`text-xs ${a.chip}`}>
                        {t(`recognitionDialog.categories.${r.category}`)}
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-wrap" data-testid={`feed-message-${r.id}`}>
                      “{r.message}”
                    </p>
                    <p className="text-xs text-muted-foreground mt-2" data-testid={`feed-sender-${r.id}`}>
                      {isES ? 'Reconocido/a por' : 'Recognized by'} <b>{r.from_user_name}</b>
                      {' · '}
                      <span className="tabular-nums">{fmtDate(r.created_at)}</span>
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
